// DeviceDetection.js - Device capability detection for performance optimization

export class DeviceCapabilityDetector {
  static detect() {
    const capabilities = {
      isLowEnd: false,
      isMobile: /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent),
      hardwareConcurrency: navigator.hardwareConcurrency || 4,
      deviceMemory: navigator.deviceMemory || 4,
      gpu: this.detectGPU(),
      webglSupport: this.checkWebGLSupport(),
      userAgent: navigator.userAgent
    };

    // Low-end device detection based on multiple factors
    // Note: Mali GPUs with good CPU specs (6+ cores) are NOT low-end
    // They just need CPU delegate instead of GPU
    // CRITICAL: Check gpu.renderer (not userAgent) for Mali detection
    // Use broader regex to catch all Mali-G variants (G31, G51, G52, G71, G72, etc.)
    const hasMaliGPU = /Mali-G\d+/i.test(capabilities.gpu.renderer);
    // NOTE: Removed RAM check - navigator.deviceMemory is unreliable on many devices
    // Samsung A8 reports 2GB when it has 8GB, making detection fail
    const hasDecentCPU = capabilities.hardwareConcurrency >= 6;

    console.log('[DeviceDetection] isLowEnd Check:', {
      hasMaliGPU,
      hasDecentCPU,
      gpu: capabilities.gpu.renderer
    });

    // Keep original safe logic with protections for non-Mali devices
    // CRITICAL: Protect Mali GPU devices from unreliable RAM readings
    capabilities.isLowEnd = (
      (capabilities.hardwareConcurrency <= 4 && !hasMaliGPU) ||
      (capabilities.deviceMemory <= 2 && !hasMaliGPU) ||  // Protect Mali GPUs from bad RAM readings
      (capabilities.gpu.tier === 'low' && !hasDecentCPU) ||
      /Galaxy Tab A|PowerVR GE8320|Adreno 505|SM-T290|SM-T295|SM-T380|SM-T385/i.test(navigator.userAgent) ||
      (this.isLowEndByPerformance() && !hasDecentCPU)
    );

    // Additional mobile-specific checks (but not for Mali GPU + good CPU)
    if (capabilities.isMobile && !(hasMaliGPU && hasDecentCPU)) {
      const wasMobileLowEnd = this.isMobileLowEnd();
      console.log('[DeviceDetection] Mobile low-end check:', wasMobileLowEnd);
      capabilities.isLowEnd = capabilities.isLowEnd || wasMobileLowEnd;
    }

    console.log('[DeviceDetection] Final isLowEnd result:', capabilities.isLowEnd);

    return capabilities;
  }

  static detectGPU() {
    const canvas = document.createElement('canvas');
    const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');

    if (!gl) {
      return { tier: 'low', renderer: 'unknown' };
    }

    const debugInfo = gl.getExtension('WEBGL_debug_renderer_info');
    let renderer = 'unknown';

    if (debugInfo) {
      renderer = gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL);
    }

    // Categorize GPU based on renderer string
    // Note: Mali-G71 (Samsung Exynos 7885) has known MediaPipe/WebGL issues
    const lowEndGPUs = [
      'Mali-G52', 'Mali-G51', 'Mali-G31', 'Mali-G71', 'Mali-G72 MP3',
      'PowerVR GE8320', 'PowerVR GE8300', 'PowerVR GT7400',
      'Adreno 610', 'Adreno 506', 'Adreno 505', 'Adreno 509',
      'Intel HD Graphics 4000', 'Intel HD Graphics 3000',
      'Intel UHD Graphics 600', 'Intel HD Graphics 400',
      'Vivante GC', 'VideoCore'
    ];

    const midRangeGPUs = [
      'Mali-G76', 'Mali-G72', 'Mali-G57',
      'Adreno 640', 'Adreno 630', 'Adreno 616',
      'PowerVR GT7600', 'PowerVR GT7800',
      'Intel Iris', 'Intel UHD Graphics 620'
    ];

    let tier = 'high';

    if (lowEndGPUs.some(gpu => renderer.includes(gpu))) {
      tier = 'low';
    } else if (midRangeGPUs.some(gpu => renderer.includes(gpu))) {
      tier = 'medium';
    }

    return { tier, renderer };
  }

  static checkWebGLSupport() {
    const canvas = document.createElement('canvas');
    const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');

    if (!gl) return { supported: false, version: null };

    const version = gl.getParameter(gl.VERSION);
    const extensions = gl.getSupportedExtensions();

    return {
      supported: true,
      version: version,
      extensions: extensions,
      maxTextureSize: gl.getParameter(gl.MAX_TEXTURE_SIZE),
      maxViewportDims: gl.getParameter(gl.MAX_VIEWPORT_DIMS)
    };
  }

  static isLowEndByPerformance() {
    // Simple performance test
    const start = performance.now();
    let iterations = 0;

    // Run a simple computation for 5ms
    while (performance.now() - start < 5) {
      Math.random() * Math.random();
      iterations++;
    }

    // If we can't do many iterations, it's likely a low-end device
    return iterations < 50000;
  }

  static isMobileLowEnd() {
    // Additional mobile-specific checks
    const userAgent = navigator.userAgent.toLowerCase();

    // Check for specific low-end Android devices
    // Note: Samsung A8 (SM-A530/SM-A730) removed - it uses MEDIUM_CPU instead
    const lowEndAndroidPatterns = [
      'sm-t290', 'sm-t295', 'sm-t380', 'sm-t385', // Samsung Tab A series
      'sm-a105', 'sm-a205', 'sm-a305', // Samsung A series (older, truly low-end)
      'redmi 7a', 'redmi 8a', 'redmi 9a', // Xiaomi budget series
      'moto e', 'moto g6 play', 'moto g7 play' // Motorola budget series
    ];

    return lowEndAndroidPatterns.some(pattern => userAgent.includes(pattern));
  }

  static getDeviceInfo() {
    const capabilities = this.detect();

    // Determine quality profile based on device capabilities
    let recommendedQuality;

    // Check for Mali GPU devices with decent CPU (like Samsung A8)
    // These GPUs have MediaPipe compatibility issues but devices often have good CPUs
    // Using broader regex to catch all Mali GPU variants
    const isMaliGPUDevice = /Mali-G\d+/i.test(capabilities.gpu.renderer);
    // NOTE: Only check CPU cores - deviceMemory API is unreliable on many devices
    const hasDecentCPU = capabilities.hardwareConcurrency >= 6;

    // Detailed logging for debugging
    console.log('[DeviceDetection] Device Detection Debug:', {
      rawGPU: capabilities.gpu.renderer,
      gpuTier: capabilities.gpu.tier,
      cores: capabilities.hardwareConcurrency,
      memory: capabilities.deviceMemory,
      isMaliGPU: isMaliGPUDevice,
      hasDecentCPU: hasDecentCPU,
      isLowEnd: capabilities.isLowEnd,
      isMobile: capabilities.isMobile
    });

    // Priority 1: Mali GPU with good CPU specs = MEDIUM_CPU
    if (isMaliGPUDevice && hasDecentCPU) {
      // Good CPU but problematic GPU → Use MEDIUM_CPU (CPU delegate with good settings)
      recommendedQuality = 'MEDIUM_CPU';
      console.log('[DeviceDetection] Using MEDIUM_CPU profile (Mali GPU + good CPU detected)');
    }
    // Priority 2: Truly low-end devices
    else if (capabilities.isLowEnd) {
      // Truly low-end device → Use LOW_END
      recommendedQuality = 'LOW_END';
      console.log('[DeviceDetection] Using LOW_END profile (low-end device detected)');
      console.log('[DeviceDetection] Why LOW_END: isLowEnd flag was set');
    }
    // Priority 3: Mid-range GPU
    else if (capabilities.gpu.tier === 'medium') {
      // Mid-range device → Use MEDIUM
      recommendedQuality = 'MEDIUM';
      console.log('[DeviceDetection] Using MEDIUM profile (mid-range GPU detected)');
    }
    // Priority 4: Default to HIGH_END
    else {
      // High-end device → Use HIGH_END
      recommendedQuality = 'HIGH_END';
      console.log('[DeviceDetection] Using HIGH_END profile (high-end device detected)');
    }

    return {
      ...capabilities,
      recommendedQuality,
      timestamp: Date.now()
    };
  }
}

// Export a singleton instance for easy access
export const deviceCapabilities = DeviceCapabilityDetector.getDeviceInfo();

// Version marker to confirm code is updated
const DETECTION_VERSION = "v2.1-mali-fix-oct15";
console.log(`[DeviceDetection] Version ${DETECTION_VERSION} loaded`);
console.log('[DeviceDetection] Device capabilities detected:', deviceCapabilities);

// Alert if Mali GPU detected with good CPU but got LOW_END
if (deviceCapabilities.recommendedQuality === 'LOW_END' &&
    /Mali-G\d+/i.test(deviceCapabilities.gpu.renderer) &&
    deviceCapabilities.hardwareConcurrency >= 6 &&
    deviceCapabilities.deviceMemory >= 3) {
  console.error('[DeviceDetection] DETECTION BUG: Mali GPU with good CPU was marked LOW_END!');
  console.error('[DeviceDetection] This should be MEDIUM_CPU. Please report this bug.');
  console.error('[DeviceDetection] GPU:', deviceCapabilities.gpu.renderer);
  console.error('[DeviceDetection] Cores:', deviceCapabilities.hardwareConcurrency);
  console.error('[DeviceDetection] RAM:', deviceCapabilities.deviceMemory);
}
